import { boundingExtent } from 'ol/extent'
import { ref, shallowRef, watch } from 'vue'

export function useMapPosition({ map, triggerUpdate }) {
  // Track last position for optimization
  const lastCenter = shallowRef(null)
  const lastZoom = shallowRef(null)
  const updateTimeout = ref(null)

  /**
   * Animate the map to a new zoom level
   */
  const zoomTo = zoom => {
    if (!map.value) return
    map.value.getView().animate({ zoom, duration: 250 })
    triggerUpdate()
  }

  /**
   * Animate the map to a new center position
   */
  const panTo = center => {
    if (!map.value) return
    map.value.getView().animate({ center, duration: 250 })
    triggerUpdate()
  }

  /**
   * Fit the map view to a bounding box
   */
  const fitTo = bbox => {
    if (!map.value || !bbox.length) return

    map.value.getView().fit(bbox, {
      padding: [50, 50, 50, 50],
      maxZoom: map.value.getView().getMaxZoom() - 2,
      duration: 250,
    })
    triggerUpdate()
  }

  /**
   * Zoom in by one level
   */
  const zoomIn = () => {
    if (!map.value) return
    const currentZoom = map.value.getView().getZoom()
    zoomTo(currentZoom + 1)
  }

  /**
   * Zoom out by one level
   */
  const zoomOut = () => {
    if (!map.value) return
    const currentZoom = map.value.getView().getZoom()
    zoomTo(currentZoom - 1)
  }

  /**
   * Fit the map view to a layer or source
   */
  const fitToLayer = layerOrSource => {
    let bbox = []

    if (layerOrSource) {
      if (typeof layerOrSource.getExtent === 'function') {
        bbox = layerOrSource.getExtent()
      } else if (
        layerOrSource.getSource &&
        typeof layerOrSource.getSource().getExtent === 'function'
      ) {
        bbox = layerOrSource.getSource().getExtent()
      }
    }

    if (bbox && bbox.length && bbox.every(coord => isFinite(coord))) {
      fitTo(bbox)
    } else {
      console.warn(
        '[MapPosition] Unable to determine valid extent for layer/source',
      )
    }
  }

  /**
   * Trigger an update event with current map position
   */
  const emitUpdate = id => {
    if (updateTimeout.value) {
      window.clearTimeout(updateTimeout.value)
    }

    updateTimeout.value = window.setTimeout(() => {
      if (!map.value) return

      const view = map.value.getView()
      const center = view.getCenter()
      const zoom = view.getZoom()

      if (
        !lastCenter.value ||
        !lastZoom.value ||
        lastZoom.value !== zoom ||
        (center && lastCenter.value && center[0] !== lastCenter.value[0]) ||
        (center && lastCenter.value && center[1] !== lastCenter.value[1])
      ) {
        lastCenter.value = center
        lastZoom.value = zoom

        if (center && zoom) {
          triggerUpdate({
            id,
            center,
            zoom,
          })
        }
      }
    }, 100)
  }

  /**
   * Clean up resources
   */
  const cleanup = () => {
    if (updateTimeout.value) {
      window.clearTimeout(updateTimeout.value)
    }
  }

  return {
    zoomTo,
    panTo,
    fitTo,
    zoomIn,
    zoomOut,
    fitToLayer,
    emitUpdate,
    cleanup,
    lastCenter,
    lastZoom,
  }
}
